import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { MessageDescriptor } from '@lingui/core';
import { 
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenuBadge
} from '@shared/ui/sidebar';
import { Avatar, AvatarFallback, AvatarImage } from '@shared/ui/avatar';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@shared/ui/dropdown-menu';
import { Badge } from '@shared/ui/badge';
import { 
  Settings, 
  LogOut, 
  User, 
  ChevronUp,
} from 'lucide-react';
import type { UserProfile } from '@shared/types/auth';
import { getRoleHomePath, type NavItem } from '@shared/lib/navigation';
import { localizePath, stripLocaleFromPathname, useLocale } from '@shared/i18n';
import { useAuth } from '@shared/hooks/useAuth';
import { useDoctorWorkspace } from '@shared/hooks/useDoctorWorkspace';
import { useIntentPrefetch } from '@shared/hooks/useIntentPrefetch';
import { useNavPrefetch } from './useNavPrefetch';

interface AppSidebarProps {
  user: UserProfile;
  navItems: NavItem[];
  roleOverride?: UserProfile['role'];
  disableNavPrefetch?: boolean;
}

const formatBadgeCount = (count: number): string => (count > 99 ? '99+' : String(count));

const getRoleDisplayName = (user: UserProfile, translate: (descriptor: MessageDescriptor) => string): string => {
  if (user.role === 'doctor' && user.organization_roles?.includes('owner')) {
    return translate({
      id: 'shared.layout.AppSidebar.role.organization-owner',
      message: 'Organization Owner',
    });
  }

  switch (user.role) {
    case 'patient':
      return translate({
        id: 'shared.layout.AppSidebar.role.patient',
        message: 'Patient',
      });
    case 'doctor':
      return translate({
        id: 'shared.layout.AppSidebar.role.doctor',
        message: 'Doctor',
      });
    case 'admin':
      return translate({
        id: 'shared.layout.AppSidebar.role.administrator',
        message: 'Administrator',
      });
    case 'super_admin':
      return translate({
        id: 'shared.layout.AppSidebar.role.super-admin',
        message: 'Super Admin',
      });
    default:
      return translate({
        id: 'shared.layout.AppSidebar.role.user',
        message: 'User',
      });
  }
};

const getRoleColor = (user: UserProfile): string => {
  if (user.role === 'doctor' && user.organization_roles?.includes('owner')) {
    return 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200';
  }

  switch (user.role) {
    case 'patient':
      return 'bg-brand-blue/10 text-brand-blue';
    case 'doctor':
      return 'bg-brand-lime/20 text-brand-lime-foreground';
    case 'admin':
      return 'bg-brand-orange/10 text-brand-orange';
    case 'super_admin':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

export const AppSidebar: React.FC<AppSidebarProps> = ({
  user,
  navItems,
  roleOverride,
  disableNavPrefetch = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { i18n } = useLingui();
  const { locale } = useLocale();
  const { getIntentHandlers } = useIntentPrefetch();
  const { prefetchPath } = useNavPrefetch();
  const { resolveDoctorPath, isEffectiveOrganizationOwner } = useDoctorWorkspace();
  const normalizedPathname = React.useMemo(
    () => stripLocaleFromPathname(location.pathname),
    [location.pathname],
  );

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  const handleProfileClick = () => {
    navigate(localizePath(resolveDoctorPath('/doctor-profile'), locale));
  };

  const getUserInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
      return t({
        id: 'shared.layout.AppSidebar.initials-fallback-dr',
        message: 'DR',
      });
    }
    return parts
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('') ||
      t({
        id: 'shared.layout.AppSidebar.initials-fallback-dr',
        message: 'DR',
      });
  };

  const roleDisplay = getRoleDisplayName(user, (descriptor) => i18n._(descriptor));
  const roleColor = getRoleColor(user);
  const canViewDoctorProfile =
    user.role === 'doctor' ||
    user.role === 'org_owner' ||
    user.organization_roles?.includes('owner') === true ||
    isEffectiveOrganizationOwner;
  const homePath = localizePath(
    resolveDoctorPath(getRoleHomePath(roleOverride ?? user.role)),
    locale,
  );
  const displayName = `${user.first_name} ${user.last_name}`.trim() || user.email;
  const headline = undefined;
  const profileEmail = user.email;
  const fallbackAvatar = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(displayName)}`;
  const avatarSrc = fallbackAvatar;
  const userInitials = getUserInitials(displayName);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to={homePath}>
                <img
                      src="/Icon_Jema_full.svg"
                      alt={t({
                        id: 'shared.layout.AppSidebar.jema-logo',
                        message: 'Jema Logo',
                      })}
                      className="w-full h-full object-contain  hover:cursor-pointer hover:bg-primary/10"
                    />
              </Link>
              {/*<Link to="/" className="flex items-center gap-2">
                <div className="flex aspect-square size-10 items-center justify-center  rounded-lg  text-primary-foreground ">
                  <img
                    src="/Icon_Jema.svg"
                    alt={t({
                      id: 'shared.layout.AppSidebar.jema-logo',

                      message: 'Jema Logo',
                    })}
                    className="size-10"
                  />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold"><Trans id='shared.layout.AppSidebar.jema-manager'>Jema Manager</Trans></span>
                  <span className="truncate text-xs text-muted-foreground"><Trans id='shared.layout.AppSidebar.medical-tourism-platform'>Medical Tourism Platform</Trans></span>
                </div>
              </Link>*/}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel><Trans id='shared.layout.AppSidebar.navigation'>Navigation</Trans></SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => {
                // Check for exact match or if pathname starts with item path followed by a slash
                // This handles nested routes like /messages/:roomSlug
                const isActive = normalizedPathname === item.path ||
                  normalizedPathname.startsWith(item.path + '/');
                const Icon = item.icon;
                const localizedItemPath = localizePath(item.path, locale);
                const intentHandlers = disableNavPrefetch
                  ? undefined
                  : getIntentHandlers(item.path, () => {
                      void prefetchPath({
                        path: item.path,
                        role: roleOverride ?? user.role,
                      });
                    });
                
                return (
                  <SidebarMenuItem key={item.key}>
                    <SidebarMenuButton 
                      asChild 
                      isActive={isActive}
                      tooltip={
                        item.description
                          ? typeof item.description === 'string'
                            ? item.description
                            : i18n._(item.description as MessageDescriptor)
                          : undefined
                      }
                    >
                      <Link
                        to={localizedItemPath}
                        className="flex items-center gap-2"
                        {...intentHandlers}
                      >
                        <Icon className="size-4" />
                        <span>
                          {typeof item.label === 'string' 
                            ? item.label 
                            : i18n._(item.label as MessageDescriptor)}
                        </span>
                        {typeof item.badge === 'number' && item.badge > 0 && (
                          <SidebarMenuBadge className="rounded-full min-w-5 h-5 px-1.5 bg-emerald-500 text-white dark:bg-emerald-400 dark:text-emerald-950 ring-2 ring-sidebar">
                            {formatBadgeCount(item.badge)}
                          </SidebarMenuBadge>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarImage 
                      src={avatarSrc}
                      alt={displayName} 
                    />
                    <AvatarFallback className="rounded-lg">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">
                      {displayName}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {headline || profileEmail}
                    </span>
                  </div>
                  <ChevronUp className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side="bottom"
                align="end"
                sideOffset={4}
              >
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarImage 
                        src={avatarSrc}
                        alt={displayName} 
                      />
                      <AvatarFallback className="rounded-lg">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 gap-1 text-sm">
                      <span className="truncate font-semibold">
                        {displayName}
                      </span>
                      <span className="truncate text-xs text-muted-foreground">
                        {profileEmail}
                      </span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="px-1 py-1.5">
                  <Badge 
                    variant="secondary" 
                    className={`text-xs ${roleColor}`}
                  >
                    {roleDisplay}
                  </Badge>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="gap-2">
                  <Settings className="size-4" />
                  <Trans id='shared.layout.AppSidebar.settings'>Settings</Trans>
                </DropdownMenuItem>
                {canViewDoctorProfile && (
                  <DropdownMenuItem className="gap-2" onClick={handleProfileClick}>
                    <User className="size-4" />
                    <Trans id='shared.layout.AppSidebar.profile'>Profile</Trans>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  className="gap-2 text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400"
                  onClick={handleSignOut}
                >
                  <LogOut className="size-4" />
                  <Trans id='shared.layout.AppSidebar.sign-out'>Sign out</Trans>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
};
